// Story 070b — frontend i18n bootstrap. Mirrors the backend's t()
// wrapper, but with:
//   - react-i18next bindings so components can `useTranslation('ns')`
//   - locale resolution chain: localStorage → /user-locale → navigator
//     → DEFAULT_LOCALE
//   - reactive language switch via i18n.changeLanguage()
//   - cross-tab sync via the `storage` event
//
// Catalogs come from `@swiss-magic/i18n` (the same resource tree the
// backend uses). For 070b we ship them eagerly — the bundle adds
// ~50-100KB per language which is acceptable for a small SPA. If
// payload becomes a concern, swap RESOURCES for i18next-http-backend
// + a small Vite plugin that serves /locales/{lng}/{ns}.json from
// the package's src/locales tree.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  ALL_NAMESPACES,
  DEFAULT_LOCALE,
  RESOURCES,
  SUPPORTED_LOCALES,
  isAnyLocale,
  isSupportedLocale,
  type AnyLocale,
} from '@swiss-magic/i18n';

const LS_LOCALE_KEY = 'swiss-magic:locale';

// Backend URL + tenant header — duplicated from lib/api/core.ts so the
// i18n bootstrap doesn't have to pull in the whole api helper (which
// transitively imports the supabase client). The /user-locale endpoint
// is the only backend call this module makes.
const API_URL =
  (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ??
  'http://localhost:3000';
function tenantHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  return { 'X-Swiss-Tenant-Host': window.location.hostname };
}

// Same {var} interpolation tokens the backend uses (matches the catalog
// authoring convention). HTML escape is disabled — i18next strings flow
// through React's JSX which already escapes, and through the email
// template engine which escapes explicitly.
const INIT_OPTS = {
  // First-paint locale: localStorage if present, else DEFAULT_LOCALE.
  // The real chain (which awaits /user-locale + checks navigator)
  // runs in `resolveLocale()` after auth bootstraps; that call
  // changeLanguages once the chain settles.
  lng: readStoredLocale() ?? DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,
  ns: ALL_NAMESPACES as unknown as string[],
  defaultNS: 'common' as const,
  resources: RESOURCES as unknown as Record<
    string,
    Record<string, Record<string, unknown>>
  >,
  interpolation: {
    escapeValue: false,
    prefix: '{',
    suffix: '}',
  },
  returnNull: false,
  returnEmptyString: false,
  // i18next's nsSeparator default is `:` (e.g. `common:buttons.save`).
  // We bake the namespace into the key as a leading dot-segment
  // (`common.buttons.save`) to match the backend's t() public
  // convention and the catalog file layout. nsSeparator: false stops
  // i18next from re-interpreting `:` in any string; we split the
  // namespace prefix manually below.
  nsSeparator: false as const,
  keySeparator: '.' as const,
};

void i18n.use(initReactI18next).init(INIT_OPTS);

// Wrap the bare i18n.t to honour the `namespace.dotted.key` convention.
// react-i18next's `useTranslation(ns).t('foo.bar')` still works for
// per-namespace lookups; this helper is for code that lives outside
// React (toasts, errors, schema validation messages).
export function tt(
  key: string,
  vars?: Record<string, string | number>,
): string {
  const dot = key.indexOf('.');
  if (dot < 0) {
    return i18n.t(key, vars) as string;
  }
  const ns = key.slice(0, dot);
  const path = key.slice(dot + 1);
  const result = i18n.t(path, { ns, ...vars }) as string;
  if (result === path) return key; // miss — surface the full key
  return result;
}

/**
 * Resolution chain after auth bootstraps:
 *   1. localStorage `swiss-magic:locale`
 *   2. `/user-locale` (when an access token is available)
 *   3. `navigator.language` best-match against supported locales
 *   4. DEFAULT_LOCALE
 * Calls `i18n.changeLanguage` if the resolved locale differs from the
 * boot-time `lng`. Idempotent.
 */
export async function resolveAndApplyLocale(
  options: {
    accessToken: string | null;
    /** Optional override for tests; defaults to navigator.language. */
    navigatorLanguage?: string | null;
  },
): Promise<AnyLocale> {
  // 1. Stored choice wins.
  const stored = readStoredLocale();
  if (stored) {
    await applyLocale(stored);
    return stored;
  }
  // 2. Backend preference for an authenticated user.
  if (options.accessToken) {
    try {
      const res = await fetch(`${API_URL}/user-locale`, {
        headers: {
          Authorization: `Bearer ${options.accessToken}`,
          ...tenantHeaders(),
        },
      });
      if (res.ok) {
        const body = (await res.json()) as { locale?: string | null };
        if (body.locale && isAnyLocale(body.locale)) {
          writeStoredLocale(body.locale);
          await applyLocale(body.locale);
          return body.locale;
        }
      }
    } catch {
      /* fall through */
    }
  }
  // 3. navigator.language best match, else hard fallback.
  const navLang =
    options.navigatorLanguage ??
    (typeof navigator !== 'undefined' ? navigator.language : null);
  const resolved = (navLang && matchNavigator(navLang)) || DEFAULT_LOCALE;

  // For an authenticated user who has never picked a language, persist
  // the browser default as their canonical locale (user_locale). This
  // makes one source of truth drive both the UI and every outbound
  // communication (emails / push / in-app) — without it the UI tracked
  // navigator each session while user_locale stayed empty, so
  // notifications fell back to a stale default. Anonymous visitors just
  // apply it for the session (no persistence — a stranger's browser on a
  // shared machine must not leave a preference behind).
  if (options.accessToken && isSupportedLocale(resolved)) {
    await setLocale(resolved, { accessToken: options.accessToken });
  } else {
    await applyLocale(resolved);
  }
  return resolved;
}

/**
 * Apply a locale change everywhere it has to ripple:
 *   - localStorage (so the next reload skips the chain)
 *   - i18n.changeLanguage (triggers re-render via react-i18next)
 *   - PUT /user-locale (when authenticated and the locale is in the
 *     user-pickable subset — xx-XX is rejected by the API)
 *   - `<html lang>` attribute for accessibility tools / Chrome reader
 *
 * Callers from the picker UI pass `accessToken` so the backend write
 * can fire; the boot-time resolveAndApplyLocale uses the no-token
 * overload internally (or just applyLocale directly).
 */
export async function setLocale(
  locale: AnyLocale,
  options?: { accessToken?: string | null },
): Promise<void> {
  writeStoredLocale(locale);
  await applyLocale(locale);
  if (options?.accessToken && isSupportedLocale(locale)) {
    // Best-effort — UI shouldn't break if the network call fails.
    try {
      await fetch(`${API_URL}/user-locale`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${options.accessToken}`,
          ...tenantHeaders(),
        },
        body: JSON.stringify({ locale }),
      });
    } catch {
      /* swallowed */
    }
  }
}


async function applyLocale(locale: AnyLocale): Promise<void> {
  if (i18n.language !== locale) {
    await i18n.changeLanguage(locale);
  }
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = locale;
  }
}

function readStoredLocale(): AnyLocale | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LS_LOCALE_KEY);
    if (raw && isAnyLocale(raw)) return raw;
  } catch {
    /* localStorage blocked */
  }
  return null;
}

function writeStoredLocale(locale: AnyLocale): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LS_LOCALE_KEY, locale);
  } catch {
    /* swallowed */
  }
}

function matchNavigator(value: string): AnyLocale | null {
  if (isAnyLocale(value)) return value;
  const base = value.split('-')[0];
  if (isAnyLocale(base)) return base;
  return null;
}

// Cross-tab sync: picker writes localStorage in tab A; the `storage`
// event fires in tab B → re-apply.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_LOCALE_KEY || !e.newValue) return;
    if (isAnyLocale(e.newValue) && e.newValue !== i18n.language) {
      void applyLocale(e.newValue);
    }
  });
}

/**
 * E2E + debugging hook — honour `?lng=<code>` on the URL. Persists the
 * choice into localStorage so the override survives navigation. Used
 * by cucumber's xx-XX smoke run to flip a single page into the
 * `[XX]`-prefixed catalog without driving the picker UI. Idempotent;
 * cleared automatically when the user picks a different locale via
 * the picker.
 */
export function applyQueryLocaleOverride(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('lng');
    if (raw && isAnyLocale(raw)) {
      writeStoredLocale(raw);
      void applyLocale(raw);
    }
  } catch {
    /* swallowed */
  }
}

// Exported so the picker UI can list "what can the user pick?". xx-XX
// is excluded from the user-visible list (test/smoke locale only) but
// remains accessible via ?lng=xx-XX query param processing.
export const PICKABLE_LOCALES = SUPPORTED_LOCALES;

// Native-language labels for the picker UI. Kept here (not in a
// catalog) on purpose: a user who can't read the current language
// still needs to find their own language in the dropdown.
export const LOCALE_LABELS: Record<AnyLocale, string> = {
  en: 'English',
  pl: 'Polski',
  de: 'Deutsch',
  'xx-XX': '[XX] Test',
};

export default i18n;
export { i18n };
